if (!customElements.get("rx-sticky-promo")) {
  customElements.define(
    "rx-sticky-promo",
    class RxStickyPromo extends HTMLElement {
      connectedCallback() {
        this.bar = this.querySelector(".rx-sp__bar");
        this.timer = this.querySelector(".rx-sp__timer");
        this.closeBtn = this.querySelector(".rx-sp__close");

        this.designMode = this.hasAttribute("data-design-mode");
        this.onExpire = this.getAttribute("data-on-expire") || "hide";
        this.expiredMessage = this.getAttribute("data-expired-message") || "";
        this.storageKey =
          this.getAttribute("data-storage-key") || "rx-sticky-promo-dismissed";
        this.endTime = Date.parse(this.getAttribute("data-end") || "");

        // Dismissed earlier and still inside the snooze window.
        if (!this.designMode && this.isDismissed()) {
          this.destroy();
          return;
        }

        // Sale already over — nothing to advertise.
        const expired = !isNaN(this.endTime) && Date.now() >= this.endTime;
        if (!this.designMode && expired && this.onExpire === "hide") {
          this.destroy();
          return;
        }

        if (isNaN(this.endTime)) {
          // Unparseable end date: keep the offer and CTA, drop the broken timer.
          if (this.timer) this.timer.remove();
          this.timer = null;
        } else {
          this.values = {
            days: this.querySelector('[data-unit="days"]'),
            hours: this.querySelector('[data-unit="hours"]'),
            minutes: this.querySelector('[data-unit="minutes"]'),
            seconds: this.querySelector('[data-unit="seconds"]'),
          };
          this.tick();
          this.interval = setInterval(() => this.tick(), 1000);
        }

        if (this.closeBtn) {
          this.closeBtn.addEventListener("click", () => this.dismiss());
        }
        this.addEventListener("click", this.onClick);

        this.measure();
        if (typeof ResizeObserver !== "undefined" && this.bar) {
          this.resizeObserver = new ResizeObserver(() => this.measure());
          this.resizeObserver.observe(this.bar);
        }

        // Slide in on the next frame so the bar never flashes in place.
        requestAnimationFrame(() => this.setAttribute("data-ready", "true"));
      }

      disconnectedCallback() {
        clearInterval(this.interval);
        if (this.resizeObserver) this.resizeObserver.disconnect();
      }

      tick() {
        const distance = this.endTime - Date.now();

        if (distance <= 0) {
          clearInterval(this.interval);
          if (this.onExpire === "hide") {
            this.destroy();
            return;
          }
          this.setAttribute("data-expired", "true");
          if (this.timer) {
            this.timer.textContent = this.expiredMessage;
            this.timer.classList.add("rx-sp__timer--expired");
          }
          this.measure();
          return;
        }

        const days = Math.floor(distance / 86400000);
        const hours = Math.floor((distance % 86400000) / 3600000);
        const minutes = Math.floor((distance % 3600000) / 60000);
        const seconds = Math.floor((distance % 60000) / 1000);

        this.setValue("days", days);
        this.setValue("hours", hours);
        this.setValue("minutes", minutes);
        this.setValue("seconds", seconds);
      }

      setValue(unit, value) {
        const el = this.values[unit];
        if (!el) return;
        const next = String(value).padStart(2, "0");
        if (el.textContent !== next) el.textContent = next;
      }

      // In-page CTAs scroll smoothly; fall back to the consultation form when
      // the configured anchor does not exist on this template.
      onClick(event) {
        const link = event.target.closest("a.rx-sp__cta");
        if (!link) return;

        const href = link.getAttribute("href") || "";
        if (href.charAt(0) !== "#") return;

        const id = href.slice(1);
        let target = id ? document.getElementById(id) : null;
        if (!target) {
          target = document.querySelector(
            ".prescription-consultation-section, .prescription-wizard",
          );
        }
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      isDismissed() {
        try {
          const until = localStorage.getItem(this.storageKey);
          return Boolean(until) && Date.now() < Number(until);
        } catch (error) {
          return false;
        }
      }

      dismiss() {
        const days = parseInt(this.getAttribute("data-dismiss-days"), 10);
        const snooze = (isNaN(days) ? 3 : days) * 86400000;
        try {
          localStorage.setItem(this.storageKey, String(Date.now() + snooze));
        } catch (error) {
          // Private browsing / storage disabled — hide for this page view only.
        }
        this.setAttribute("data-ready", "false");
        setTimeout(() => this.destroy(), 320);
      }

      destroy() {
        clearInterval(this.interval);
        document.documentElement.style.setProperty("--rx-sticky-promo-h", "0px");
        this.remove();
      }

      // Publish the bar height so page content can reserve room for it.
      measure() {
        const height = this.bar ? this.bar.offsetHeight : 0;
        document.documentElement.style.setProperty(
          "--rx-sticky-promo-h",
          height + "px",
        );
      }
    },
  );
}
